import React from "react";
import { motion } from "framer-motion";
import {
  FileText,
  Camera,
  ShieldCheck,
  ClipboardCheck,
  Lock,
  Monitor,
  DoorOpen,
  Radio,
} from "lucide-react";
import images from "../../Data/images";

const features = [
  {
    title: "Surveillance Camera Systems",
    description:
      "CCTV systems provide continuous monitoring and documentation across your property. Argus installs high-definition surveillance systems including interior and exterior cameras, parking lot monitoring, perimeter coverage, night vision, video recording, and remote viewing capabilities.",
    icon: <Camera className="w-8 h-8 text-red-600" />,
  },
  {
    title: "Access Control Systems",
    description:
      "Access control technology allows property owners to manage who enters buildings and restricted areas. Argus installs keycard systems, keypad access, electronic door locks, and secure entry systems with activity logs and tracking.",
    icon: <Lock className="w-8 h-8 text-red-600" />,
  },
  {
    title: "Mobile Surveillance & Remote Monitoring",
    description:
      "Advanced monitoring tools allow security teams to oversee properties even when guards are not physically present. Argus utilizes mobile surveillance cameras, remote monitoring systems, digital reporting tools, and real-time communication with dispatch.",
    icon: <Monitor className="w-8 h-8 text-red-600" />,
  },
  {
    title: "Drone Security Technology",
    description:
      "Drone technology provides rapid visual monitoring of large areas. Argus uses drones for large property surveillance, construction site monitoring, perimeter inspections, and property assessments.",
    icon: <DoorOpen className="w-8 h-8 text-red-600" />,
  },
  {
    title: "Technology-Supported Security Personnel",
    description:
      "Argus guards are equipped with modern tools including digital incident reporting systems, GPS patrol verification, photo documentation, and real-time communication with dispatch for improved transparency and accountability.",
    icon: <Radio className="w-8 h-8 text-red-600" />,
  },
  {
    title: "Integrated Security Solutions",
    description:
      "Argus combines security personnel with advanced technology including CCTV systems, mobile patrol services, access control, and digital reporting tools to deliver a layered and highly effective security program.",
    icon: <ShieldCheck className="w-8 h-8 text-red-600" />,
  },
  {
    title: "Electronic Reporting System",
    description:
      "Guards can investigate incidents, gather information, attach photos, complete reports, and send them to clients within minutes. Clients can access reports through a secure portal.",
    icon: <FileText className="w-8 h-8 text-red-600" />,
  },
];

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.2,
      duration: 0.6,
      ease: "easeOut",
    },
  }),
};

const TechnologySection = () => {
  return (
    <section className="py-16 px-4 md:px-10 lg:px-20">
      <div className="max-w-6xl mx-auto">
        {/* Heading */}
        <motion.h2
          className="text-3xl md:text-4xl font-bold text-gray-800 border-b-4 border-red-600 inline-block mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          Modern Security Powered by Technology
        </motion.h2>

        {/* Intro */}
        <motion.p
          className="text-gray-700 text-lg mb-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          Security today is about more than physical presence. Modern security
          operations rely on technology, real-time information, and advanced
          monitoring systems to improve protection and response.
        </motion.p>

        <motion.p
          className="text-gray-700 text-lg mb-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.6 }}
        >
          Argus Security International integrates innovative security
          technologies with trained personnel to deliver more effective and
          accountable protection for our clients. By combining human expertise
          with modern tools, we provide greater visibility, faster response, and
          improved documentation for property managers and businesses.
        </motion.p>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 gap-10">
          {features.map((feature, i) => (
            <motion.div
              key={feature.title}
              className="bg-white shadow-md rounded-2xl p-6 flex gap-4"
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <div>{feature.icon}</div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-1">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-base leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Images Section */}
        {/* <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-16"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <img
            src={images.s2robot}
            alt="Reporting System"
            className="w-full h-64 object-cover rounded-xl shadow"
          />
          <img
            src={images.p2Robot}
            alt="Cameras"
            className="w-full h-64 object-cover rounded-xl shadow"
          />
        </motion.div> */}
      </div>
    </section>
  );
};

export default TechnologySection;
